import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../providers/AuthContext';
import { useDeviceId } from './use-device-id.hook';
export const useWebSocketConnection = (options) => {
    const { url, autoConnect = true, reconnection = true, reconnectionAttempts = 5, reconnectionDelay = 1000, applicationName } = options;
    const { getAuthorizationToken } = useAuth();
    const deviceId = useDeviceId();
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const socketRef = useRef(null);
    const messageHandlersRef = useRef(new Map());
    const connect = useCallback(() => {
        if (socketRef.current?.connected) {
            return;
        }
        setConnectionStatus('connecting');
        socketRef.current = io(url, {
            transports: ['websocket'],
            reconnection,
            reconnectionAttempts,
            reconnectionDelay,
            auth: { token: getAuthorizationToken(), applicationName, deviceId }
        });
        const socket = socketRef.current;
        socket.on('connect', () => {
            setConnectionStatus('connected');
        });
        socket.on('disconnect', () => {
            setConnectionStatus('disconnected');
        });
        socket.on('connect_error', e => {
            console.log('connect_error', e);
            setConnectionStatus('error');
        });
        // Setup message handlers
        messageHandlersRef.current.forEach((handlers, eventName) => {
            handlers.forEach(handler => {
                socket.on(eventName, handler);
            });
        });
    }, [url, reconnection, reconnectionAttempts, reconnectionDelay, getAuthorizationToken, applicationName, deviceId]);
    const disconnect = useCallback(() => {
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
            setConnectionStatus('disconnected');
        }
    }, []);
    const emit = useCallback((eventName, data) => {
        if (socketRef.current?.connected) {
            socketRef.current.emit(eventName, data);
        }
    }, []);
    const subscribe = useCallback((eventName, handler) => {
        if (!messageHandlersRef.current.has(eventName)) {
            messageHandlersRef.current.set(eventName, new Set());
        }
        messageHandlersRef.current.get(eventName).add(handler);
        // If socket is already connected, add the handler immediately
        if (socketRef.current?.connected) {
            socketRef.current.on(eventName, handler);
        }
        // Return unsubscribe function
        return () => {
            const handlers = messageHandlersRef.current.get(eventName);
            if (handlers) {
                handlers.delete(handler);
                if (handlers.size === 0) {
                    messageHandlersRef.current.delete(eventName);
                }
            }
            if (socketRef.current) {
                socketRef.current.off(eventName, handler);
            }
        };
    }, []);
    useEffect(() => {
        if (autoConnect) {
            connect();
        }
        return () => {
            disconnect();
        };
    }, [autoConnect, connect, disconnect]);
    return {
        connectionStatus,
        connect,
        disconnect,
        emit,
        subscribe,
        isConnected: connectionStatus === 'connected',
        isConnecting: connectionStatus === 'connecting',
        isDisconnected: connectionStatus === 'disconnected',
        hasError: connectionStatus === 'error'
    };
};
//# sourceMappingURL=use-web-socket-connection.hook.js.map